import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';

export interface SearchableOption {
  value: any;
  description: string;
}

@Component({
  selector: 'app-searchable-select',
  standalone: false,
  templateUrl: './searchable-select.html',
  styleUrl: './searchable-select.scss',
})
export class SearchableSelect implements OnChanges {
  @Input() options: SearchableOption[] = [];
  @Input() selected: any[] = [];
  @Input() label = '';
  @Input() icon = '';
  @Input() hint = '';
  @Input() multiple = true;

  @Output() selectedChange = new EventEmitter<any[]>();

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild('trigger') autoTrigger!: MatAutocompleteTrigger;

  searchCtrl = new FormControl('');
  filteredOptions: SearchableOption[] = [];
  focused = false;

  // Igual ao UF: só reseta quando as options mudam, não quando selected muda
  ngOnChanges(changes: SimpleChanges) {
    if (changes['options']) {
      this.filteredOptions = [...this.options];
    }
  }

  // Igual ao UF: removeAccents
  private removeAccents(str: string): string {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  // Igual ao UF: filterUfs() — lê direto do nativeElement, lowercase, sem acentos
  filter() {
    const raw = this.searchInput?.nativeElement.value || this.searchCtrl.value || '';
    const term = this.removeAccents(raw.trim().toLowerCase());
    this.filteredOptions = this.options.filter((o) =>
      this.removeAccents(o.description.toLowerCase()).includes(term) ||
      this.removeAccents(String(o.value).toLowerCase()).includes(term)
    );
  }

  // Igual ao UF: isUfSelected()
  isSelected(value: any): boolean {
    return this.selected.includes(value);
  }

  // Método para obter description do value
  getDescription(value: any): string {
    const option = this.options.find(opt => opt.value === value);
    return option ? option.description : value;
  }

  // Igual ao UF: addUf() — toggle, limpa input, reseta filtro, devolve foco, reabre painel
  select(event: any) {
    const val = event.option.value;

    if (this.multiple) {
      if (this.selected.includes(val)) {
        this.selected = this.selected.filter((v) => v !== val);
      } else {
        this.selected = [...this.selected, val];
      }
    } else {
      this.selected = [val];
    }

    this.selectedChange.emit(this.selected);

    // Igual ao UF: limpa FormControl + nativeElement + reseta filtro
    this.searchCtrl.setValue('');
    if (this.searchInput) {
      this.searchInput.nativeElement.value = '';
    }
    this.filteredOptions = [...this.options];

    // Igual ao UF: devolve foco e reabre painel
    setTimeout(() => {
      this.searchInput?.nativeElement.focus();
      if (this.multiple) {
        this.autoTrigger?.openPanel();
      }
    });
  }

  // Igual ao UF: removeUf()
  remove(val: any) {
    this.selected = this.selected.filter((v) => v !== val);
    this.selectedChange.emit(this.selected);
  }

  // Igual ao UF: clearUfSearch() — verifica se ainda tem foco antes de limpar
  clearSearch() {
    setTimeout(() => {
      if (document.activeElement === this.searchInput?.nativeElement) return;
      this.searchCtrl.setValue('');
      if (this.searchInput) {
        this.searchInput.nativeElement.value = '';
      }
      this.filteredOptions = [...this.options];
      this.focused = false;
    }, 200);
  }

  // Igual ao UF: onFocus + openPanel
  onFocus() {
    this.focused = true;
    this.autoTrigger?.openPanel();
  }

  // Igual ao UF: focusInput via click no chipGrid
  focusInput() {
    this.searchInput?.nativeElement.focus();
  }
}
